import { ExternalLink, Loader2, UserStar } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { USER_LINK_ACCOUNTS_PATH } from "@/shared/configs/routes.config";
import { useIsEmployeeAccountLinked } from "@/shared/hooks/useIsEmployeeAccountLinked";
import { BENEF_APP_URL, createBenefAutoLoginUrl } from "@/shared/services/benefAutoLogin.service";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/ui/Tooltip";

type BeneficiarySpaceLinkProps = { collapsed: boolean };

const COLLAPSED_CLASS = "flex size-8 items-center justify-center self-center rounded-md border border-primary/30 text-primary transition-colors hover:border-primary/50 hover:bg-primary/5";
const EXPANDED_CLASS = "flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground bg-gray-100 transition-colors hover:bg-gray-200";

const BeneficiarySpaceLink = ({ collapsed }: BeneficiarySpaceLinkProps) => {
  const navigate = useNavigate();
  const isLinked = useIsEmployeeAccountLinked();
  const [isLoading, setIsLoading] = useState(false);

  // Tant que le compte n'est pas rattaché, l'espace bénéficiaire n'est
  // pas exploitable : on propose plutôt le rattachement.
  const label = isLinked ? "Espace bénéficiaire" : "Rattacher mon compte";
  const ariaLabel = isLinked ? `${label} (ouvre dans un nouvel onglet)` : label;

  const handleClick = async () => {
    if (isLoading) return;

    // ADM-640: si le compte n'est pas encore rattaché, on navigue vers
    // la page de rattachement de la console (navigation interne) au lieu
    // d'ouvrir le benef sur `/user/settings/rattacher-comptes` — route
    // qui n'existe pas côté benef et renvoyait donc un 404.
    if (!isLinked) {
      navigate(USER_LINK_ACCOUNTS_PATH);
      return;
    }

    setIsLoading(true);
    try {
      window.open(await createBenefAutoLoginUrl(), "_blank");
    }
    catch {
      window.open(BENEF_APP_URL, "_blank");
      toast.error("Connexion automatique impossible", { description: "Vous avez été redirigé vers la page de connexion." });
    }
    finally {
      setIsLoading(false);
    }
  };

  const icon = isLoading
    ? <Loader2 size={16} className="animate-spin" />
    : <UserStar size={16} />;

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" onClick={handleClick} aria-label={ariaLabel} className={COLLAPSED_CLASS}>
            {icon}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <button type="button" onClick={handleClick} aria-label={ariaLabel} className={`${EXPANDED_CLASS} w-full text-left cursor-pointer`}>
      {icon}
      <span className="truncate">{label}</span>
      {isLinked && <ExternalLink size={14} className="ml-auto" />}
    </button>
  );
};

export default BeneficiarySpaceLink;
